/* eslint-disable max-lines */
import React from 'react';
import cn from 'clsx';
import { XIcon } from '@fronton/icons-react';
import { SpecialOfferModalStructure } from 'modules/model-json';
import { createElementKey } from 'utils';
import { strictBreakpoints } from '../../../styles/breakpoints';
import { Button, ButtonWithGAClickButton } from '../../button';
import { Modal } from '../../modal';
import { SectionSubTitle } from '../../section-subtitle';
import { Typography } from '../../typography';
import {
  specialOfferModal,
  specialOfferModalBody,
  specialOfferModalBodyWrapper,
  specialOfferModalButton,
  specialOfferModalCloseButton,
  specialOfferModalContent,
  specialOfferModalDisclaimer,
  specialOfferModalDisclaimerText,
  specialOfferModalImage,
  specialOfferModalImageWrapper,
  specialOfferModalOptionList,
  specialOfferModalOptionMarkerTypeMap,
  specialOfferModalSubTitle,
  specialOfferModalText,
  specialOfferModalTitle,
} from './special-offer-modal-styles';

type SpecialOfferModalProps = {
  elementBlock: string;
  elementName: string;
  modalContent: SpecialOfferModalStructure;
  isOpen: boolean;
  onClose: () => void;
  onModalBtnClick?: () => void;
  regionId: string | undefined;
};

const makeUrlsClickable = (text: string) =>
  text.replace(/link/g, 'a').replace(/&lt;/g, '<').replace(/&gt;/g, '>');

export const SpecialOfferModal: React.FC<SpecialOfferModalProps> = ({
  elementBlock,
  elementName,
  modalContent,
  isOpen,
  onClose,
  onModalBtnClick,
  regionId,
}) => {
  // Вставляем код региона, если сслыка региональная
  const btnHref = regionId
    ? modalContent.button?.link?.replace('regionId', regionId)
    : modalContent.button?.link;
  const handleButtonClick = () => {
    // Если ссылки нет, вызываем стандартный экшен нажатия кнопки
    if (!btnHref && onModalBtnClick) {
      onModalBtnClick();
    }
    onClose();
  };

  return (
    <Modal
      className={specialOfferModal}
      closeButton={false}
      onClose={onClose}
      show={isOpen}
      size="l"
      variant="curtain">
      <Button
        adaptive={false}
        size="s"
        className={specialOfferModalCloseButton}
        iconOnly
        variant="pseudo"
        label="Close"
        rounded
        onClick={onClose}>
        <XIcon type="outline" size="l" color="text-invert" />
      </Button>

      <div className={specialOfferModalContent}>
        <div className={specialOfferModalImageWrapper}>
          <picture>
            <source
              srcSet={modalContent.media.bannerLargeRef}
              media={strictBreakpoints['--only-large-desktop']}
            />
            <source
              srcSet={modalContent.media.bannerDesktopRef}
              media={strictBreakpoints['--only-desktop']}
            />
            <source srcSet={modalContent.media.bannerTabletRef} media={strictBreakpoints['--only-tablet']} />
            <img
              alt={modalContent.media.creative}
              className={specialOfferModalImage}
              src={modalContent.media.bannerMobileRef}
            />
          </picture>
        </div>

        <div className={specialOfferModalBodyWrapper}>
          <div className={specialOfferModalBody}>
            <SectionSubTitle adaptive margin="s" as="h2" variant="h1">
              {modalContent.modalTitle}
            </SectionSubTitle>
            {modalContent.title && (
              <Typography className={specialOfferModalTitle} variant="h3">
                {modalContent.title}
              </Typography>
            )}
            {modalContent.subTitle && (
              <Typography className={specialOfferModalSubTitle} variant="m" size="body_accent">
                {modalContent.subTitle}
              </Typography>
            )}

            {modalContent.topText &&
              modalContent.topText.map(
                (text, index) =>
                  text && (
                    <Typography
                      as="p"
                      className={cn(specialOfferModalText, specialOfferModalSubTitle)}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: makeUrlsClickable(text) }}
                      key={createElementKey('topText', index)}
                      size="body_long"
                      variant="m"
                    />
                  )
              )}

            {modalContent.optionList.length > 0 && (
              <ul
                className={cn(
                  specialOfferModalOptionList,
                  specialOfferModalOptionMarkerTypeMap[modalContent.optionList[0].description]
                )}>
                {modalContent.optionList.map(
                  (option, index) =>
                    option.title && (
                      <li key={createElementKey('option', index)}>
                        <Typography
                          className={specialOfferModalText}
                          dangerouslySetInnerHTML={{ __html: makeUrlsClickable(option.title) }}
                          size="body_long"
                          variant="m"
                        />
                      </li>
                    )
                )}
              </ul>
            )}

            {modalContent.bottomText &&
              modalContent.bottomText.map(
                (text, index) =>
                  text && (
                    <Typography
                      as="p"
                      className={specialOfferModalSubTitle}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: makeUrlsClickable(text) }}
                      key={createElementKey('bottomText', index)}
                      size="body_long"
                      variant="m"
                    />
                  )
              )}

            {modalContent.button?.label && (
              <div className={specialOfferModalButton}>
                <ButtonWithGAClickButton
                  elementBlock={elementBlock}
                  elementName={elementName}
                  elementText={modalContent.button.label}
                  {...modalContent.button}
                  href={btnHref}
                  onClick={handleButtonClick}
                >
                  {modalContent.button.label}
                </ButtonWithGAClickButton>
              </div>
            )}

            {modalContent.disclaimer && (
              <div className={specialOfferModalDisclaimer}>
                {modalContent.disclaimer.map(
                  (text, index) =>
                    text && (
                      <Typography
                        className={specialOfferModalDisclaimerText}
                        color="text-minor"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: makeUrlsClickable(text) }}
                        key={createElementKey('disclaimer', index)}
                        variant="s"
                        size="body_short"
                      />
                    )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
